import CurrencyFormat from "react-currency-format";
import type { V2ProjectResource } from "@generated";
import type { PendingSummary } from "../utils/pendingSummary";

export type ChangeOrderSummary = NonNullable<
  V2ProjectResource["attributes"]["change-order-summary"]
>;

interface Props {
  summary: ChangeOrderSummary;
  pending: PendingSummary;
  currency: string;
}

const Money = ({ value, prefix }: { value: number; prefix: string }) => (
  <CurrencyFormat
    displayType="text"
    prefix={prefix}
    isNumericString
    thousandSeparator
    value={value.toFixed(2)}
  />
);

// The parent's revenue as signed, the counted change-order delta, and the
// two combined. The API computes revenue_including_change_orders as the
// parent's own (cached) revenue plus delta_revenue, so subtracting the delta
// recovers "as signed" on the same basis as the other two figures. Pending
// change requests aren't in the summary; the list works those out.
const SummaryInfo = ({ summary, pending, currency }: Props) => {
  const including = Number(summary.revenue_including_change_orders ?? 0);
  const delta = Number(summary.delta_revenue ?? 0);
  const asSigned = including - delta;

  return (
    <div className="change-requests-summary" data-testid="change-order-summary">
      <div className="change-requests-summary__cell" data-cell="as-signed">
        <div className="change-requests-summary__label">As signed</div>
        <div className="change-requests-summary__value">
          <Money value={asSigned} prefix={currency} />
        </div>
      </div>
      <div className="change-requests-summary__cell" data-cell="approved">
        <div className="change-requests-summary__label">
          Approved change requests ({summary.counted_count ?? 0})
        </div>
        <div className="change-requests-summary__value change-requests-summary__value--positive">
          <Money value={delta} prefix={`+${currency}`} />
        </div>
      </div>
      <div className="change-requests-summary__cell" data-cell="total">
        <div className="change-requests-summary__label">
          Project total with approved changes
        </div>
        <div className="change-requests-summary__value">
          <Money value={including} prefix={currency} />
        </div>
      </div>
      <div
        className={`change-requests-summary__cell change-requests-summary__cell--pending${
          pending.count === 0
            ? " change-requests-summary__cell--none-pending"
            : ""
        }`}
        data-cell="pending"
      >
        {pending.count === 0 ? (
          "No open change requests"
        ) : (
          // One inline span: the cell is a flex box, which would drop the
          // space before the amount.
          <span>
            {pending.count} change{" "}
            {pending.count === 1 ? "request" : "requests"} not yet approved,
            worth <Money value={pending.revenue} prefix={`+${currency}`} />
          </span>
        )}
      </div>
    </div>
  );
};

export default SummaryInfo;
